import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/ui/tooltip.tsx";
import type { HistoricalOrder } from "#/hooks/use-hyperliquid-account";
import { cn } from "#/lib/utils";
import {
	formatCamelDashLabel,
	formatDetailedDateTime,
	formatPrice,
	formatSize,
} from "../../lib/formatters";
import { getOrderHistoryStatusUi } from "../../lib/hyperliquid-error";
import { numericString } from "./lib/sorting-fns";

const col = createColumnHelper<HistoricalOrder>();

function getOrderTypeKey(orderType: string): string | null {
	const map: Record<string, string> = {
		Limit: "order.type.limit",
		"Limit order": "order.type.limit",
		Market: "order.type.market",
		"Market order": "order.type.market",
		"Stop limit": "order.type.stop-limit",
		"Stop Limit": "order.type.stop-limit",
		"Stop market": "order.type.stop-market",
		"Stop Market": "order.type.stop-market",
		"Take profit limit": "order.type.tp-limit",
		"Take Profit Limit": "order.type.tp-limit",
		"Take profit market": "order.type.tp-market",
		"Take Profit Market": "order.type.tp-market",
		TWAP: "order.type.twap",
	};
	return map[orderType] ?? null;
}

export const orderHistoryColumns = [
	col.accessor("statusTimestamp", {
		id: "statusTimestamp",
		header: () => <T keyName="order-history.columns.time" />,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-faint">
				{formatDetailedDateTime(getValue())}
			</span>
		),
	}),
	col.accessor("order.coin", {
		id: "coin",
		header: () => <T keyName="order-history.columns.coin" />,
		cell: ({ getValue }) => (
			<span className="text-label2-medium text-trade-text-strong">
				{getValue()}-PERP
			</span>
		),
	}),
	col.accessor("order.side", {
		id: "side",
		header: () => <T keyName="order-history.columns.side" />,
		cell: ({ getValue }) => {
			const isBuy = getValue() === "B";
			return (
				<span className={isBuy ? "text-trade-green" : "text-trade-red"}>
					{isBuy ? <T keyName="general.buy" /> : <T keyName="general.sell" />}
				</span>
			);
		},
	}),
	col.accessor("order.orderType", {
		id: "type",
		header: () => <T keyName="order-history.columns.type" />,
		cell: ({ getValue }) => {
			const orderType = getValue();
			const orderTypeKey = getOrderTypeKey(orderType);

			return (
				<span className="text-trade-text-secondary">
					{orderTypeKey ? (
						<T keyName={orderTypeKey} />
					) : (
						formatCamelDashLabel(orderType)
					)}
				</span>
			);
		},
	}),
	col.accessor("order.limitPx", {
		id: "price",
		header: () => <T keyName="order-history.columns.price" />,
		sortingFn: numericString,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatPrice(getValue())}
			</span>
		),
	}),
	col.accessor("order.origSz", {
		id: "size",
		header: () => <T keyName="order-history.columns.size" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatSize(getValue())} {row.original.order.coin}
			</span>
		),
	}),
	col.accessor("status", {
		header: () => <T keyName="order-history.columns.status" />,
		cell: ({ getValue }) => {
			const status = getValue();
			const statusUi = getOrderHistoryStatusUi(status);
			const label = statusUi.labelKey ? (
				<T keyName={statusUi.labelKey} />
			) : (
				formatCamelDashLabel(status)
			);
			const trigger = (
				<span
					className={cn(
						statusUi.colorClass,
						statusUi.detailKey &&
							"decoration-dotted underline underline-offset-3",
					)}
				>
					{label}
				</span>
			);

			if (!statusUi.detailKey) return trigger;

			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<span>{trigger}</span>
					</TooltipTrigger>
					<TooltipContent side="top">
						<T keyName={statusUi.detailKey} />
					</TooltipContent>
				</Tooltip>
			);
		},
	}),
];
